"use client";

import { FC, useState, useEffect } from "react";
import { TrustBadge } from "./TrustBadge";

type G2TrustBadgeProps = {
  reviewUrl?: string;
  badgeUrl?: string;
  alt?: string;
  title?: string;
  width: number;
  height: number;
};

const safeOrigin = (url: string): string | null => {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

/**
 * Renders a G2 review/award badge.
 *
 * - The G2 badge stays live (loaded directly from g2.com to respect their
 *   impression-tracked, `max-age=0` widget); its `preconnect`/`dns-prefetch`
 *   hints are hoisted into <head> by React so the connection warms early, and
 *   only on the pages where a G2 badge actually renders.
 * - The G2 badge only renders if the user has given consent for the C0003
 *   category (Functional Cookies) in OneTrust.
 */
export const G2TrustBadge: FC<G2TrustBadgeProps> = ({
  reviewUrl,
  badgeUrl,
  alt,
  title,
  width,
  height,
}) => {
  const [hasC0003Consent, setHasC0003Consent] = useState(false);

  useEffect(() => {
    const updateConsent = () => {
      const entry = document.cookie
        .split(/;\s*/)
        .find((cookie) => cookie.startsWith("OptanonConsent="));

      if (!entry) {
        setHasC0003Consent(false);
        return;
      }

      try {
        const value = decodeURIComponent(entry.slice("OptanonConsent=".length));
        const groups = new URLSearchParams(value).get("groups");
        setHasC0003Consent(groups?.split(",").includes("C0003:1") ?? false);
      } catch {
        setHasC0003Consent(false);
      }
    };

    updateConsent();
    window.addEventListener("OneTrustGroupsUpdated", updateConsent);
    window.addEventListener("OTConsentApplied", updateConsent);

    return () => {
      window.removeEventListener("OneTrustGroupsUpdated", updateConsent);
      window.removeEventListener("OTConsentApplied", updateConsent);
    };
  }, []);

  if (!badgeUrl || !reviewUrl) {
    return null;
  }

  if (!hasC0003Consent) {
    return null;
  }

  const preconnectOrigin = safeOrigin(badgeUrl);
  const accessibleAlt = alt || `Read reviews on G2`;

  return (
    <>
      {preconnectOrigin && (
        <>
          {/* Hoisted into <head> by React so the connection warms early. */}
          <link rel="dns-prefetch" href={preconnectOrigin} />
          <link rel="preconnect" href={preconnectOrigin} />
        </>
      )}
      <TrustBadge
        reviewUrl={reviewUrl}
        src={badgeUrl}
        alt={accessibleAlt}
        title={title}
        width={width}
        height={height}
      />
    </>
  );
};
